import { Box, Button, Typography, Alert as MuiAlert, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { Dayjs } from 'dayjs'
import { CardBox } from '../../../theme/styleComponents'
import { ContentHead } from '../../common/ContentHead'
import { Alert } from '../../common/Alert'
import { OverlayLoader } from '../../common/OverlayLoader'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { CloseMonthModal } from './CloseMonthModal'
import { UncloseMonthModal } from './UncloseMonthModal'
import { PeriodStatusListing } from './PeriodStatusListing'
import { PeriodSummaryCards } from './PeriodSummaryCards'
import { YearEndStatusCard } from './YearEndStatusCard'
import { AccountingPeriod, CloseAccountingPeriodInput, YearEndClose } from './types'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import {
  PeriodStatus,
  useAccountingPeriodsLazyQuery,
  useCloseAccountingPeriodMutation,
  useReCloseYearMutation,
  useUncloseAccountingPeriodMutation,
  useYearEndCloseLazyQuery
} from '../../../../generated'

export const AccountingPeriodClose = () => {
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear())
  const [fiscalYearDate, setFiscalYearDate] = useState<Dayjs | null>(dayjs())
  const [accountingPeriods, setAccountingPeriods] = useState<AccountingPeriod[]>([])
  const [yearEndClose, setYearEndClose] = useState<YearEndClose | null>(null)
  const [previousOfFiscalYearClosed, setPreviousOfFiscalYearClosed] = useState(false)
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [showUncloseModal, setShowUncloseModal] = useState(false)
  const [periodToUnclose, setPeriodToUnclose] = useState<AccountingPeriod | null>(null)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [showRecloseConfirm, setShowRecloseConfirm] = useState(false)

  const [fetchAccountingPeriods, { loading: fetchLoading, refetch }] = useAccountingPeriodsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: { fiscalYear },
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      if (data?.accountingPeriods) {
        setAccountingPeriods(data.accountingPeriods)
      }
    }
  })

  const [fetchYearEndClose, { loading: yearEndLoading }] = useYearEndCloseLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError(error) {
      // Don't show error if year is simply not closed yet
      if (!error.message.includes('not found')) {
        Alert.error(error.message)
      }
    },
    onCompleted(data) {
      setYearEndClose(data?.yearEndClose || null)
    }
  })

  const [fetchPreviousYearClosed] = useYearEndCloseLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError(error) {
      if (!error.message.includes('not found')) {
        Alert.error(error.message)
      }
    },
    onCompleted(data) {
      setPreviousOfFiscalYearClosed(!!data?.yearEndClose?.fiscalYear)
    }
  })

  const [closeAccountingPeriod, { loading: closeLoading }] = useCloseAccountingPeriodMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      if (data?.closeAccountingPeriod) {
        const monthName = data.closeAccountingPeriod.month
        const year = data.closeAccountingPeriod.fiscalYear
        const status = data.closeAccountingPeriod.status
        const month = data.closeAccountingPeriod.month

        // Different message based on whether period was fully closed or just an account
        if (status === PeriodStatus.Closed) {
          if (month === 12) {
            Alert.success(
              `Successfully closed December ${year}. Year ${year} has been automatically closed and year-end processing is complete.`
            )
          } else {
            Alert.success(`Successfully closed all accounts for ${monthName}/${year}`)
          }
        } else {
          Alert.success(`Successfully closed selected account for ${monthName}/${year}. Period remains OPEN.`)
        }

        setShowCloseModal(false)
        refetch({ fiscalYear })

        // Refetch year-end data if December was closed
        if (month === 12) {
          fetchYearEndClose({ variables: { fiscalYear: year } })
        }
      }
    }
  })

  const [uncloseAccountingPeriod, { loading: uncloseLoading }] = useUncloseAccountingPeriodMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted() {
      if (periodToUnclose) {
        Alert.success(`Successfully unclosed ${periodToUnclose.month}/${periodToUnclose.fiscalYear}`)
        setShowUncloseModal(false)
        const wasDecember = periodToUnclose.month === 12
        const year = periodToUnclose.fiscalYear
        setPeriodToUnclose(null)
        refetch({ fiscalYear })

        // Refetch year-end data if December was unclosed (year might be reopened)
        if (wasDecember) {
          fetchYearEndClose({ variables: { fiscalYear: year } })
        }
      }
    }
  })

  const [reCloseYear, { loading: recloseLoading }] = useReCloseYearMutation({
    onError(error: Error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      if (data?.reCloseYear) {
        Alert.success(`Successfully re-closed Year ${data.reCloseYear.fiscalYear}.`)
        setShowRecloseConfirm(false)
        refetch({ fiscalYear })
        fetchYearEndClose({ variables: { fiscalYear } })
      }
    }
  })

  const handleCloseMonth = (input: CloseAccountingPeriodInput) => {
    closeAccountingPeriod({
      variables: { input }
    })
  }

  const handleUncloseMonth = () => {
    if (periodToUnclose) {
      uncloseAccountingPeriod({
        variables: {
          input: {
            fiscalYear: periodToUnclose.fiscalYear,
            month: periodToUnclose.month
          }
        }
      })
    }
  }

  const openUncloseModal = (period: AccountingPeriod) => {
    setPeriodToUnclose(period)
    setShowUncloseModal(true)
  }

  const closeUncloseModal = () => {
    setShowUncloseModal(false)
    setPeriodToUnclose(null)
  }

  const handleConfirmReclose = () => {
    reCloseYear()
  }

  // Update fiscalYear when fiscalYearDate changes
  useEffect(() => {
    if (fiscalYearDate) {
      setFiscalYear(fiscalYearDate.year())
    }
  }, [fiscalYearDate])

  useEffect(() => {
    fetchAccountingPeriods({
      variables: { fiscalYear }
    })
    fetchYearEndClose({
      variables: { fiscalYear }
    })
  }, [fiscalYear, fetchAccountingPeriods, fetchYearEndClose])

  useEffect(() => {
    if (fiscalYear) {
      const previousYear = fiscalYear - 1
      fetchPreviousYearClosed({
        variables: { fiscalYear: previousYear }
      })
    }
  }, [fiscalYear, fetchPreviousYearClosed])

  const closedPeriodsCount = accountingPeriods.filter(p => p.status === PeriodStatus.Closed).length

  return (
    <>
      {successMessage && (
        <MuiAlert severity="success" onClose={() => setSuccessMessage('')} sx={{ mx: 0, mt: 2 }}>
          {successMessage}
        </MuiAlert>
      )}

      <Box display="flex" flexDirection="column" mt={2}>
        <Typography variant="h5">Accounting Period Close</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Close monthly accounting periods to create balance snapshots and maintain financial integrity.
        </Typography>
      </Box>

      <CardBox marginTop={3}>
        <ContentHead
          title="Period Status"
          customButton={() => (
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setShowRecloseConfirm(true)}
                // DOESN'T ALLOW RE-CLOSE ADV DATA PERIODS
                disabled={!previousOfFiscalYearClosed || fiscalYear <= 2026}
              >
                Re-Close Previous Year
              </Button>

              <Button
                variant="contained"
                // DOESN'T ALLOW CLOSE ADV DATA PERIODS
                disabled={!!yearEndClose || fiscalYear < 2026}
                onClick={() => setShowCloseModal(true)}
                color="primary"
              >
                Close Month
              </Button>
            </Box>
          )}
        />

        <Box display="flex" justifyContent="space-between" alignItems="center" m={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={['year']}
              label="Fiscal Year"
              value={fiscalYearDate}
              onChange={value => {
                setFiscalYearDate(value)
              }}
              renderInput={params => (
                <TextField {...params} sx={{ minWidth: 200 }} inputProps={{ ...params.inputProps, readOnly: true }} />
              )}
              maxDate={dayjs()}
              minDate={dayjs().subtract(7, 'year')}
            />
          </LocalizationProvider>

          <Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Closed Periods:</strong> {closedPeriodsCount} / 12 months
            </Typography>
          </Box>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <YearEndStatusCard fiscalYear={fiscalYear} yearEndClose={yearEndClose} loading={yearEndLoading} />

          <PeriodSummaryCards periods={accountingPeriods} fiscalYear={fiscalYear} />

          <PeriodStatusListing
            periods={accountingPeriods}
            fiscalYear={fiscalYear}
            onUnclose={openUncloseModal}
            loading={fetchLoading}
          />
        </Box>

        {(closeLoading || uncloseLoading || recloseLoading) && <OverlayLoader />}
      </CardBox>

      <CloseMonthModal
        isOpen={showCloseModal}
        fiscalYear={fiscalYear}
        onClose={() => setShowCloseModal(false)}
        onConfirm={handleCloseMonth}
        accountingPeriods={accountingPeriods}
        isLoading={closeLoading}
      />

      <UncloseMonthModal
        isOpen={showUncloseModal}
        period={periodToUnclose}
        onClose={closeUncloseModal}
        onConfirm={handleUncloseMonth}
        isLoading={uncloseLoading}
      />

      <ConfirmationModal
        isOpen={showRecloseConfirm}
        title="Re-Close Previous Year"
        message="This will recalculate retained earnings and year-end totals of last closed year and refresh the next year beginning balances. This action cannot be undone."
        confirmText="Confirm Re-Close"
        cancelText="Cancel"
        variant="danger"
        isLoading={recloseLoading}
        onConfirm={handleConfirmReclose}
        onCancel={() => setShowRecloseConfirm(false)}
      />
    </>
  )
}
